import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  ApprovalLevel,
  ApproverType,
  getLeaveWorkflow,
  saveLeaveWorkflow,
} from '../../../network/approvalWorkflow';
import { usePeopleOptions } from '../../../hooks/usePeopleOptions';
import { useToast } from '../../../utils/useToast';
import { Button } from '../../../ui/Button';

const APPROVER_LABELS: Record<ApproverType, string> = {
  manager: "The requester's manager",
  hr_admin: 'Any HR admin',
  user: 'A specific person',
};

const MAX_STEPS = 5;

function errorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/**
 * Settings > Approvals: the steps a leave request goes through. With no
 * steps, requests are decided by the requester's manager or an HR admin,
 * exactly as before. Each step must approve before the next one is asked;
 * any decline ends the request.
 */
export function ApprovalWorkflowSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const workflowQuery = useQuery(['leave-workflow'], getLeaveWorkflow, {
    retry: false,
  });
  const { people } = usePeopleOptions();
  const [levels, setLevels] = useState<ApprovalLevel[]>([]);

  useEffect(() => {
    setLevels(workflowQuery.data?.levels ?? []);
  }, [workflowQuery.data]);

  const save = useMutation((next: ApprovalLevel[]) => saveLeaveWorkflow(next), {
    onSuccess: () => {
      queryClient.invalidateQueries(['leave-workflow']);
      toast({
        title: 'Saved',
        description: 'Approval steps updated.',
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Save failed',
        description: errorMessage(error, 'Could not save the approval steps.'),
        variant: 'error',
      });
    },
  });

  const update = (index: number, patch: Partial<ApprovalLevel>) =>
    setLevels((current) =>
      current.map((level, i) => {
        if (i !== index) return level;
        const next = { ...level, ...patch };
        if (next.approver_type !== 'user') delete next.user_id;
        return next;
      }),
    );

  const move = (index: number, offset: number) =>
    setLevels((current) => {
      const target = index + offset;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const incomplete = levels.some(
    (level) => level.approver_type === 'user' && !level.user_id,
  );

  if (workflowQuery.isLoading) {
    return <p className="text-sm text-gray-500">Loading approval steps…</p>;
  }
  if (workflowQuery.isError) {
    return (
      <p className="text-sm text-red-600">
        Couldn't load the approval steps. Refresh to try again.
      </p>
    );
  }

  return (
    <div className="space-y-6" data-testid="approval-workflow-settings">
      <p className="text-sm text-gray-600">
        {levels.length === 0
          ? "No approval steps: each request is decided by the requester's manager or an HR admin."
          : 'Requests go through these steps in order. Every step must approve; a decline at any step ends the request.'}
      </p>

      <ol className="space-y-3">
        {levels.map((level, index) => (
          <li
            key={index}
            className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 p-3"
          >
            <span className="w-16 text-sm font-medium text-gray-700">
              Step {index + 1}
            </span>
            <label className="sr-only" htmlFor={`approver-type-${index}`}>
              Approver for step {index + 1}
            </label>
            <select
              id={`approver-type-${index}`}
              className="rounded-md border-gray-300 text-sm"
              value={level.approver_type}
              onChange={(event) =>
                update(index, {
                  approver_type: event.target.value as ApproverType,
                })
              }
            >
              {(Object.keys(APPROVER_LABELS) as ApproverType[]).map((type) => (
                <option key={type} value={type}>
                  {APPROVER_LABELS[type]}
                </option>
              ))}
            </select>
            {level.approver_type === 'user' && (
              <>
                <label className="sr-only" htmlFor={`approver-user-${index}`}>
                  Person for step {index + 1}
                </label>
                <select
                  id={`approver-user-${index}`}
                  className="rounded-md border-gray-300 text-sm"
                  value={level.user_id ?? ''}
                  onChange={(event) =>
                    update(index, { user_id: event.target.value || undefined })
                  }
                >
                  <option value="">Choose a person…</option>
                  {people.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name}
                    </option>
                  ))}
                </select>
              </>
            )}
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move step ${index + 1} up`}
              >
                Up
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => move(index, 1)}
                disabled={index === levels.length - 1}
                aria-label={`Move step ${index + 1} down`}
              >
                Down
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() =>
                  setLevels((current) => current.filter((_, i) => i !== index))
                }
                aria-label={`Remove step ${index + 1}`}
              >
                Remove
              </Button>
            </div>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline" size="sm"
          onClick={() =>
            setLevels((current) => [...current, { approver_type: 'manager' }])
          }
          disabled={levels.length >= MAX_STEPS}
        >
          Add step
        </Button>
        <Button
          onClick={() => save.mutate(levels)}
          disabled={save.isLoading || incomplete}
        >
          {save.isLoading ? 'Saving…' : 'Save approval steps'}
        </Button>
      </div>
      {incomplete && (
        <p className="text-sm text-amber-700">
          Choose a person for every "A specific person" step before saving.
        </p>
      )}
      <p className="text-xs text-gray-500">
        If a step names nobody who can decide (for example, the requester has
        no manager), it goes to the company's HR admins so no request gets
        stuck. Changes apply to new requests.
      </p>
    </div>
  );
}
